import { usePageContext } from '@context/PageContext.tsx';
import React, { createContext, use, useCallback, useMemo, useState } from 'react';
import { DecklineRowData } from 'types/Page.types';
import { HighlightingContextType, HighlightingState } from 'types/RowHighlighting.types';

const initialState: HighlightingState = {
  modifiedRows: new Set(),
};

const HighlightingContext = createContext<HighlightingContextType | null>(null);

/**
 * Provides shared row highlighting functionality across multiple components
 */
export const HighlightingProvider = ({ children }: { children: React.ReactNode }) => {
  const [state, setState] = useState<HighlightingState>(initialState);
  const { setPageRowData } = usePageContext();

  /**
   * Generic method to apply highlighting to rows based on a predicate function.
   * @param pageRowData - Array of row data to apply highlighting to
   * @param predicate - Function that determines if a row should be highlighted
   * @param highlightKey - The key to set for highlighting (defaults to 'is_subtotal_highlighted')
   */
  const applyHighlighting = useCallback(
    (
      pageRowData: DecklineRowData[],
      predicate: (row: DecklineRowData) => boolean,
      highlightKey: keyof DecklineRowData = 'is_subtotal_highlighted',
    ) => {
      // Only update pageRowData if highlighting actually changes to avoid infinite re-render loops.
      // Effects that depend on pageRowData call this function, and unconditionally calling
      // setPageRowData would create a new array reference, re-triggering those effects.
      const hasChanges = pageRowData.some((row) => row[highlightKey] !== predicate(row));
      if (!hasChanges) return;

      const updatedPageRowData = pageRowData.map((row) => ({
        ...row,
        [highlightKey]: predicate(row),
      }));
      setPageRowData(updatedPageRowData);
    },
    [setPageRowData],
  );

  /**
   * Clears all subtotal highlighting from the provided row data.
   * @param pageRowData - Array of row data to clear highlighting from
   */
  const clearSubtotalHighlighting = useCallback(
    (pageRowData: DecklineRowData[]) => {
      applyHighlighting(pageRowData, () => false, 'is_subtotal_highlighted');
    },
    [applyHighlighting],
  );

  // State checker
  const isRowModified = useCallback(
    (rowId: string) => {
      return state.modifiedRows.has(rowId);
    },
    [state.modifiedRows],
  );

  /**
   * Marks a row as modified by adding its ID to the modified rows set.
   * @param rowId - The unique identifier of the row to mark as modified
   */
  const markRowAsModified = useCallback((rowId: string) => {
    setState((prev) => ({
      modifiedRows: new Set(prev.modifiedRows).add(rowId),
    }));
  }, []);

  /**
   * Removes a row from the modified rows set.
   * @param rowId - The unique identifier of the row to remove from modified state
   */
  const removeRowFromModified = useCallback((rowId: string) => {
    setState((prev) => {
      const newModifiedRows = new Set(prev.modifiedRows);
      newModifiedRows.delete(rowId);
      return { modifiedRows: newModifiedRows };
    });
  }, []);

  /**
   * Clears all highlighting and modified row states, optionally triggering a refresh callback.
   * @param refreshCallback - Optional callback function to execute after clearing state
   */
  const clearAllHighlightingWithRefresh = useCallback((refreshCallback?: () => void) => {
    setState(initialState);
    if (refreshCallback) {
      refreshCallback();
    }
  }, []);

  /**
   * Returns the appropriate CSS class for a row based on its modification state.
   * @param rowId - The unique identifier of the row
   * @returns CSS class string ('modified-row' if modified, empty string otherwise)
   */
  const getRowHighlightClass = useCallback(
    (rowId: string) => {
      const isModified = isRowModified(rowId);
      return isModified ? 'modified-row' : '';
    },
    [isRowModified],
  );

  const contextValue = useMemo<HighlightingContextType>(
    () => ({
      isRowModified,
      markRowAsModified,
      removeRowFromModified,
      clearAllHighlightingWithRefresh,
      getRowHighlightClass,
      applyHighlighting,
      clearSubtotalHighlighting,
    }),
    [
      isRowModified,
      markRowAsModified,
      removeRowFromModified,
      clearAllHighlightingWithRefresh,
      getRowHighlightClass,
      applyHighlighting,
      clearSubtotalHighlighting,
    ],
  );

  return <HighlightingContext value={contextValue}>{children}</HighlightingContext>;
};

/**
 * Hook to access the highlighting context
 * @throws {Error} If used outside HighlightingProvider
 * @returns {HighlightingContextType} The highlighting context value
 */
export const useHighlightingContext = (): HighlightingContextType => {
  const context = use(HighlightingContext);
  if (!context) {
    throw new Error('useHighlightingContext must be used within a HighlightingProvider');
  }
  return context;
};
